import { useState, useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAuthState } from "@/v2/hooks/useAuthState";
import { Loader2 } from "lucide-react";

dayjs.extend(utc);
dayjs.extend(customParseFormat);
import { V2Shell } from "@/v2/components/V2Shell";
import { PageHeader } from "@/v2/components/PageHeader";
import { SidebarLayout } from "@/v2/components/layout/SidebarLayout";
import useConfig from "@/hooks/useConfig";
import { UserProfileData } from "./api";
import { NeedHelpCard } from "@/v2/pages/FavoritesManager/components/right-rail/NeedHelpCard";
import { ProfileInformationSection } from "./components/ProfileInformationSection";
import { SecuritySection } from "./components/SecuritySection";
import { AccountDetailsSection } from "./components/AccountDetailsSection";
import { TipsCard } from "./components/TipsCard";

function ProfileContent() {
  const configData = useConfig();

  // Profile data comes from the v2 auth store (localStorage, kept fresh by the
  // profile mutations via notifyAuthChanged). GetUserAccountDetails is a billing
  // endpoint (Stripe CustomerDetails) and does not carry profile fields.
  // `user` is memoized per auth snapshot, so this useMemo caches correctly.
  const { user } = useAuthState();
  const profile: UserProfileData = useMemo(
    () => ({
      UserID: user?.userId ?? "",
      FirstName: user?.firstName ?? "",
      LastName: user?.lastName ?? "",
      Email: user?.email ?? "",
      ProfileImage: user?.profileImage ?? "",
      createdAt: user?.createdAt ?? "",
      IsEmailVerified: user?.isEmailVerified ?? false,
    }),
    [user],
  );
  const isProfileLoading = false;

  // Local state for First Name and Last Name inputs
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Seed the name inputs once from the store; a later store update (e.g. a
  // background refresh) must not clobber the user's in-progress edits.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!seededRef.current && (profile.FirstName || profile.LastName)) {
      setFirstName(profile.FirstName);
      setLastName(profile.LastName);
      seededRef.current = true;
    }
  }, [profile.FirstName, profile.LastName]);

  // Date Formatting Helper. The server sends createdAt as
  // "YYYY-MM-DD hh:mm:ss.SSSSSS A Z" (ET), which new Date() cannot parse, so
  // parse it with dayjs + customParseFormat (the v2-sanctioned date lib, as in
  // Events/date-utils), trying the known shapes.
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const normalized = dateStr.trim().toUpperCase();
    for (const fmt of [
      "YYYY-MM-DD hh:mm:ss.SSSSSS A Z",
      "YYYY-MM-DD HH:mm:ss.SSSSSS Z",
      "YYYY-MM-DD HH:mm:ss",
    ]) {
      const parsed = dayjs.utc(normalized, fmt);
      if (parsed.isValid()) return parsed.format("MMM DD, YYYY");
    }
    const fallback = dayjs(dateStr);
    return fallback.isValid() ? fallback.format("MMM DD, YYYY") : "";
  };

  const memberSinceDate = profile?.createdAt
    ? formatDate(profile.createdAt)
    : "";

  return (
    <SidebarLayout>
      <div className="w-full px-4 pb-16 text-left">
        {isProfileLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-action" />
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 items-start w-full">
            <div className="flex-1 w-full min-w-0 flex flex-col">
              {/* Header - Left Column Only */}
              <PageHeader
                title="Profile Information"
                subtitle={`Manage your personal information and how you sign in to ${configData?.title || "NA TV"}.`}
              />

              <div className="w-full bg-card text-card-foreground border border-border rounded-xl divide-y divide-border">
                <ProfileInformationSection
                  profile={profile}
                  firstName={firstName}
                  lastName={lastName}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                />
                <SecuritySection />
                <AccountDetailsSection memberSinceDate={memberSinceDate} />
              </div>
            </div>

            {/* Right side widgets */}
            <div className="w-full lg:w-[260px] flex-shrink-0 flex flex-col gap-4 static lg:sticky lg:top-0">
              {/* Need Help? Card */}
              <NeedHelpCard />
              {/* Tips Card */}
              <TipsCard />
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
}

export default function Profile() {
  return (
    <V2Shell>
      <ProfileContent />
    </V2Shell>
  );
}
